import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Lock, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Friendly error extraction per auth playbook
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      // Auth context update will re-render parent route guard.
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-4 py-10"
      data-testid="admin-login-page"
    >
      <div className="relative z-10 w-full max-w-md">
        {/* Back link */}
        <button
          onClick={() => navigate("/")}
          data-testid="login-back-home"
          className="mb-6 inline-flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.2em] font-semibold text-[#6B6A68] hover:text-[#EB5E28] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to the parlour
        </button>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white border-2 border-[#1A1A1A] shadow-[8px_8px_0px_#1A1A1A]"
        >
          {/* Header strip */}
          <div className="bg-[#1A1A1A] text-[#F4F1EA] px-5 py-3 flex items-center justify-between border-b-4 border-[#EB5E28]">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#EB5E28]" />
              <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] font-bold text-[#EB5E28]">
                Editorial Desk
              </span>
            </div>
            <span className="font-serif-display italic text-sm">Private</span>
          </div>

          <div className="p-6 sm:p-8">
            <p className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] font-semibold mb-2">
              Restricted
            </p>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold leading-tight">
              Sign in.
            </h1>
            <p className="font-serif-display italic text-base sm:text-lg text-[#6B6A68] mt-1.5">
              Admin access only.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
              <div>
                <label
                  htmlFor="email"
                  className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B6A68] mb-1.5"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-3 font-sans-ui text-base focus:outline-none focus:ring-2 focus:ring-[#EB5E28]"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B6A68] mb-1.5"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-3 pr-11 font-sans-ui text-base focus:outline-none focus:ring-2 focus:ring-[#EB5E28]"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((p) => !p)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="login-toggle-password"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#6B6A68] hover:text-[#1A1A1A]"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {err && (
                <div
                  className="border-2 border-[#842029] bg-[#F8D7DA] text-[#842029] px-3 py-2 font-sans-ui text-sm"
                  data-testid="login-error"
                  role="alert"
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !email || !password}
                data-testid="login-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#EB5E28] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-sm shadow-[4px_4px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Sign in
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>

        <p className="mt-5 font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] text-center">
          Locked after 5 failed attempts · 15 min cool-off
        </p>
      </div>
    </div>
  );
}
